/**
 * HelpOverlay — כפתור עזרה + drawer, ללא עטיפת children.
 * מוטמע ב-VerticalLayout לצד <Outlet /> כדי שכשל בעזרה לא יפיל את שאר האפליקציה.
 */
import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { JobRole } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import { HelpButton, HelpDrawer } from './components';
import { findHelpEntry } from './registry/helpRegistry';

// ─── URL → helpKey ────────────────────────────────────────────────────────────

function pathnameToHelpKey(pathname: string): string | undefined {
  const segments = pathname
    .replace(/^\//, '')
    .split('/')
    .filter(Boolean)
    .filter((seg) => !/^\d+$/.test(seg));  // הסר IDs מספריים

  if (segments.length === 0) return undefined;
  return segments.join('-');
}

// ─── ErrorBoundary סביב כל מנגנון העזרה ─────────────────────────────────────

interface ErrorBoundaryState { hasError: boolean }

class HelpErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return null;  // כשל בעזרה — לא מציג כלום (לא קורס האפליקציה)
    return this.props.children;
  }
}

// ─── HelpOverlay — כפתור + drawer בלבד ───────────────────────────────────────

const HelpOverlay: React.FC = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const roleId = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => LocalstorageService.getItem(JobRole) as number | null,
    []
  );

  const helpKey = useMemo(() => {
    const candidate = pathnameToHelpKey(pathname);
    if (!candidate) return undefined;
    return findHelpEntry(candidate) ? candidate : undefined;
  }, [pathname]);

  // הצג כפתור רק אם יש מדריך תפקיד או עזרה הקשרית
  if (!helpKey && roleId == null) return null;

  return (
    <>
      <HelpButton
        onClick={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      <HelpDrawer
        helpKey={helpKey}
        roleId={roleId ?? undefined}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

// ─── HelpProvider — עוטף HelpOverlay ב-ErrorBoundary ─────────────────────────

export const HelpProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>
    {children}
    <HelpErrorBoundary>
      <HelpOverlay />
    </HelpErrorBoundary>
  </>
);
